/**
 * Step 2 — Select EDD Formats.
 *
 * A full browser of all 196 catalog formats PLUS the org's own saved Custom EDDs, with checkboxes
 * (MULTI-select), a search box, and quick filters (All / State / Federal / Custom). Each SELECTED
 * format shows a mini summary — "NYSDEC EDD v5 — 120 fields, CSV, needs cross-table".
 *
 * The shipped catalog comes from `useTemplates`; the lab's own configs come from `useFormats` with
 * `source: "lab"` (the static catalog never includes user-created configs, so without this a custom
 * EDD a user just built would be invisible in the picker). Catalog formats are generatable only when
 * they have a usable config (`has_config`); a saved config always is.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTemplates } from "../../api/templates";
import { useFormats } from "../../api/formats";
import type { FormatItem, TemplateSummary } from "../../api/types";
import { Badge } from "../../components/Badge";
import { Modal } from "../../components/Modal";
import { Spinner } from "../../components/Spinner";
import { TextField } from "../../components/TextField";
import { ConfigCrossTablesPanel } from "../editor/ConfigCrossTablesPanel";
import type { SelectedFormat } from "./types";
import { CrossTableReadiness } from "./CrossTableReadiness";

type QuickFilter = "all" | "state" | "federal" | "custom";

const QUICK_FILTERS: { key: QuickFilter; label: string; hint: string }[] = [
  { key: "all", label: "All formats", hint: "Every catalog format" },
  { key: "state", label: "State", hint: "State agency deliverables" },
  { key: "federal", label: "Federal", hint: "Federal / EPA deliverables" },
  { key: "custom", label: "Custom", hint: "Your lab's own configs" },
];

/**
 * One pickable format — a shipped catalog template OR one of the org's own saved Custom EDDs (a
 * `ConfigRecord`). Both are normalized into this shape so they list together.
 */
interface PickerFormat {
  id: string; // SelectedFormat id: raw config uuid for configs, catalog template id for templates
  name: string;
  family: string;
  hay: string; // lowercase family/system/jurisdiction, for the state/federal quick filters
  outputFormat: string;
  outputLabel?: string;
  fieldCount: number;
  generatable: boolean; // catalog templates only when they have a usable config; configs always
  isCustom: boolean;
  needsCrossTable: boolean;
  kind: "template" | "config";
  selector: { template?: string; config_id?: string };
}

function fromTemplate(t: TemplateSummary): PickerFormat {
  const hay = `${t.family} ${t.system} ${t.jurisdiction}`.toLowerCase();
  return {
    id: t.id,
    name: t.name,
    family: t.family,
    hay,
    outputFormat: t.output_format,
    outputLabel: t.output_label,
    fieldCount: t.field_count,
    generatable: t.has_config, // spec-only catalog entries can't be generated
    isCustom: t.kind === "config" || /custom|lab/.test(hay),
    // "needs cross-table" heuristic from the catalog family/system (the real flag lives on the
    // resolved config; this is a helpful hint, not a hard claim).
    needsCrossTable: /cross|xtable/i.test(`${t.system} ${t.family}`),
    kind: "template",
    selector: { template: t.id },
  };
}

function fromConfig(c: FormatItem): PickerFormat {
  // `/formats` ids are "config:<uuid>"; the wizard identifies a config by its RAW uuid (matching the
  // mid-wizard "create a new format" auto-select), so prefer config_id.
  const configId = c.config_id ?? c.id;
  return {
    id: configId,
    name: c.name,
    family: c.family,
    hay: c.family.toLowerCase(),
    outputFormat: c.output_format,
    outputLabel: c.output_label,
    fieldCount: c.field_count,
    generatable: true, // a saved config is always runnable (needs_crosstable is a warning, not a block)
    isCustom: true,
    needsCrossTable: c.status === "needs_crosstable",
    kind: "config",
    selector: { config_id: configId },
  };
}

function matchesFilter(t: PickerFormat, f: QuickFilter): boolean {
  if (f === "all") return true;
  if (f === "custom") return t.isCustom;
  if (f === "state") return /state|dec|deq|dep|dnr|dhec|nysdec|njdep|tceq/.test(t.hay);
  if (f === "federal") return /federal|epa|usgs|dod|navy|army|geotracker/.test(t.hay);
  return true;
}

/** "42 fields · 4 files · TXT". Renders the backend's `output_label` when present: it is the
 *  only value that knows the separator and the EQuIS 4-file deliverable. The DELIMITED→"CSV"
 *  fallback below is for older responses only — it told every tab-delimited EDD, and the
 *  EQuIS 4-file set, that they were a single CSV. */
export function summaryLine(t: {
  name: string;
  field_count: number;
  output_format: string;
  output_label?: string;
}): string {
  const label =
    t.output_label?.trim() ||
    ((t.output_format || "DELIMITED").toUpperCase() === "DELIMITED"
      ? "CSV"
      : (t.output_format || "").toUpperCase());
  return `${t.field_count} fields · ${label}`;
}

export function FormatSelectStep({
  selected,
  onChange,
  onCreateNewFormat,
}: {
  selected: SelectedFormat[];
  onChange: (next: SelectedFormat[]) => void;
  /** When provided, shows a "Create a new format" button that hands off to the format editor and
   *  returns the user here with the new format selected. */
  onCreateNewFormat?: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<QuickFilter>("all");
  // A saved config (config_id selector) the user is editing cross-tables for, in a modal. Only
  // config-backed selections support per-template cross-tables; catalog templates use the org store.
  const [xtableTarget, setXtableTarget] = useState<{ id: string; name: string } | null>(null);
  const templatesQ = useTemplates({ q: query, limit: 200 });
  // The org's own saved Custom EDDs. No location_id: the backend scopes by role (techs are forced to
  // their location + org-level configs; admins see every location's), so the picker shows all the
  // custom EDDs this user can run. This is what makes a freshly-created config appear here.
  const configsQ = useFormats({ source: "lab", q: query, limit: 200 });

  const items = useMemo(() => {
    const configs = (configsQ.data?.items ?? []).map(fromConfig);
    const templates = (templatesQ.data?.items ?? []).map(fromTemplate);
    // The user's own configs first (most relevant), then the shipped catalog.
    return [...configs, ...templates].filter((f) => matchesFilter(f, filter));
  }, [configsQ.data, templatesQ.data, filter]);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const toggle = (fmt: PickerFormat) => {
    if (!fmt.generatable) return; // spec-only catalog formats can't be generated
    if (selectedIds.has(fmt.id)) {
      onChange(selected.filter((s) => s.id !== fmt.id));
    } else {
      onChange([
        ...selected,
        {
          id: fmt.id,
          selector: fmt.selector,
          name: fmt.name,
          family: fmt.family,
          outputFormat: fmt.outputFormat,
          outputLabel: fmt.outputLabel,
          fieldCount: fmt.fieldCount,
          needsCrossTable: fmt.needsCrossTable,
          kind: fmt.kind,
        },
      ]);
    }
  };

  return (
    <div className="stack">
      <div className="row row--between row--wrap" style={{ gap: "var(--space-2)" }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>Choose your EDD formats</h2>
          <p className="muted" style={{ margin: 0 }}>
            Pick one or more of the {templatesQ.data?.total ?? 196} formats. We'll generate each one
            for every work order you uploaded.
          </p>
        </div>
        {onCreateNewFormat && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCreateNewFormat}>
            + Create a new format
          </button>
        )}
      </div>

      <div className="row row--wrap" style={{ gap: "var(--space-2)", alignItems: "flex-end" }}>
        <TextField
          label="Search formats"
          placeholder="e.g. NYSDEC, EPA, GeoTracker…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <div className="filter-chips" role="group" aria-label="Quick filters">
          {QUICK_FILTERS.map((q) => (
            <button
              key={q.key}
              type="button"
              title={q.hint}
              className={`chip ${filter === q.key ? "chip--active" : ""}`.trim()}
              aria-pressed={filter === q.key}
              onClick={() => setFilter(q.key)}
            >
              {q.label}
            </button>
          ))}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="card card--pad-sm stack" style={{ gap: "var(--space-1)" }}>
          <strong>Selected ({selected.length})</strong>
          {selected.map((s) => (
            <div key={s.id} className="row row--between row--wrap" style={{ gap: "var(--space-2)" }}>
              <span>
                <strong>{s.name}</strong>{" "}
                <span className="faint">
                  —{" "}
                  {summaryLine({
                    name: s.name,
                    field_count: s.fieldCount ?? 0,
                    output_format: s.outputFormat ?? "",
                    output_label: s.outputLabel,
                  })}
                </span>
                {" "}
                <CrossTableReadiness
                  template={s.selector.template ?? s.selector.config_id}
                  configId={s.selector.config_id}
                />
              </span>
              <span className="row" style={{ gap: "var(--space-1)" }}>
                {s.selector.config_id && (
                  <button
                    type="button"
                    className="chip"
                    onClick={() =>
                      setXtableTarget({ id: s.selector.config_id!, name: s.name })
                    }
                  >
                    {t("editor.crossTableValues.manageShort")}
                  </button>
                )}
                <button
                  type="button"
                  className="chip"
                  onClick={() => onChange(selected.filter((x) => x.id !== s.id))}
                >
                  Remove
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={xtableTarget !== null}
        onClose={() => setXtableTarget(null)}
        title={t("editor.crossTableValues.manageFor", { name: xtableTarget?.name ?? "" })}
      >
        {xtableTarget && (
          <ConfigCrossTablesPanel configId={xtableTarget.id} readOnly={false} />
        )}
      </Modal>

      {templatesQ.isLoading ? (
        <div className="center-pad">
          <Spinner size="lg" label="Loading the format catalog…" />
        </div>
      ) : (
        <div
          className="stack"
          style={{ gap: "var(--space-1)", maxHeight: 420, overflow: "auto" }}
          role="group"
          aria-label="EDD formats"
        >
          {items.map((fmt) => (
            <label
              key={fmt.id}
              className="checkbox"
              style={{ opacity: fmt.generatable ? 1 : 0.55, alignItems: "flex-start" }}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(fmt.id)}
                disabled={!fmt.generatable}
                onChange={() => toggle(fmt)}
              />
              <span className="stack" style={{ gap: 0 }}>
                <span>
                  <strong>{fmt.name}</strong>{" "}
                  {fmt.family && <Badge tone="default">{fmt.family}</Badge>}
                  {!fmt.generatable && (
                    <span className="faint"> — spec only (not generatable yet)</span>
                  )}
                </span>
                <span className="faint">
                  {summaryLine({
                    name: fmt.name,
                    field_count: fmt.fieldCount,
                    output_format: fmt.outputFormat,
                    output_label: fmt.outputLabel,
                  })}
                </span>
              </span>
            </label>
          ))}
          {items.length === 0 && (
            <p className="muted">No formats match your search and filter.</p>
          )}
        </div>
      )}
    </div>
  );
}
